import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionStatus = ({ user = null, className = "" }) => {
  const getSubscriptionInfo = () => {
    if (!user) {
      return {
        type: 'guest',
        title: 'Guest Access',
        description: 'Sign in to unlock your career development journey',
        icon: 'User',
        color: 'muted-foreground',
        bgColor: 'bg-muted',
        features: [
          'Limited plan preview',
          'Basic assessment access',
          'No progress tracking'
        ],
        cta: 'Sign In',
        ctaPath: '/authentication'
      };
    }

    if (user?.isPremium) {
      const daysUntilRenewal = user?.subscriptionEndsAt 
        ? Math.ceil((new Date(user.subscriptionEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

      return {
        type: 'premium',
        title: 'Premium Member',
        description: daysUntilRenewal 
          ? `Renews in ${daysUntilRenewal} days`
          : 'Active subscription',
        icon: 'Crown',
        color: 'warning',
        bgColor: 'bg-warning/10',
        features: [
          '3 Career Assessments per month (included)',
          'Unlimited Saved Plans',
          'Unlimited Exercises',
          'AI-Powered Career Insights & Recommendations',
          'Resume Builder with Skills & Experience Suggestions',
          'Salary Insights by ZIP Code & Occupation',
          'Personalized Development Plans',
          'Export Capabilities (PDF, Word, Shareable Links)',
          'Access to All 900+ Occupations',
          'Priority Email Support'
        ],
        cta: 'Manage Subscription',
        ctaPath: '/subscription-management'
      };
    }

    return {
      type: 'free',
      title: 'Free Account',
      description: 'Upgrade to unlock full potential',
      icon: 'User',
      color: 'secondary',
      bgColor: 'bg-secondary/10',
      features: [
        '1 Career Assessment',
        '1 Saved Plan',
        '2 Exercises per Week',
        'AI Plan (1/mo, short-term 2–4 weeks, basic)'
      ],
      excludedFeatures: [
        '3 Career Assessments per month (included)',
        'Unlimited Saved Plans',
        'Unlimited Exercises',
        // AI Plan is available to free tier; do not list as excluded
        'Resume Builder with Skills & Experience Suggestions',
        'Salary Insights by ZIP Code & Occupation',
        'Personalized Development Plans',
        'Export Capabilities (PDF, Word, Shareable Links)',
        'Access to All 900+ Occupations',
        'Priority Email Support'
      ],
      cta: 'Upgrade to Premium',
      ctaPath: '/subscription-management'
    };
  };

  const subscriptionInfo = getSubscriptionInfo();

  const premiumBenefits = [
    {
      icon: 'Brain',
      title: 'Unlimited AI Plans',
      description: 'Generate as many career plans as you need'
    },
    {
      icon: 'BarChart3',
      title: 'Advanced Analytics',
      description: 'Deep insights into your progress and trends'
    },
    {
      icon: 'Target',
      title: 'Custom KPIs',
      description: 'Track personalized career metrics'
    },
    {
      icon: 'Download',
      title: 'Export Features',
      description: 'Download reports in PDF and CSV formats'
    },
    {
      icon: 'Calendar',
      title: 'Weekly Check-ins',
      description: 'Automated progress summaries and reminders'
    },
    {
      icon: 'Headphones',
      title: 'Priority Support',
      description: '24/7 dedicated customer support'
    }
  ];

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      {/* Current Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${subscriptionInfo?.bgColor}`}>
            <Icon name={subscriptionInfo?.icon} size={20} className={`text-${subscriptionInfo?.color}`} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{subscriptionInfo?.title}</h3>
            <p className="text-sm text-muted-foreground">{subscriptionInfo?.description}</p>
          </div>
        </div>
        
        <Button
          variant={subscriptionInfo?.type === 'free' ? 'default' : 'outline'}
          size="sm"
          iconName={subscriptionInfo?.type === 'premium' ? 'Settings' : subscriptionInfo?.type === 'free' ? 'Crown' : 'LogIn'}
          iconPosition="left"
          asChild
        >
          <Link to={subscriptionInfo?.ctaPath}>{subscriptionInfo?.cta}</Link>
        </Button>
      </div>
      {/* Current Features */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-foreground">
          {subscriptionInfo?.type === 'guest' ? 'Available with Account' : 'Your Features'}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {subscriptionInfo?.features?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <Icon 
                name={subscriptionInfo?.type === 'guest' ? 'Lock' : 'Check'} 
                size={14} 
                className={subscriptionInfo?.type === 'guest' ? 'text-muted-foreground' : 'text-success'} 
              />
              <span className="text-muted-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Excluded Features for Free Account */}
      {subscriptionInfo?.type === 'free' && subscriptionInfo?.excludedFeatures && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">
            Not Included
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {subscriptionInfo?.excludedFeatures?.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <Icon 
                  name="X" 
                  size={14} 
                  className="text-red-500" 
                />
                <span className="text-muted-foreground line-through">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Premium Benefits for Non-Premium Users */}
      {subscriptionInfo?.type !== 'premium' && (
        <div className="space-y-4 border-t border-border pt-4">
          <div className="flex items-center space-x-2">
            <Icon name="Crown" size={16} className="text-warning" />
            <h4 className="text-sm font-semibold text-foreground">Premium Benefits</h4>
          </div>
          
          <div className="grid gap-3">
            {premiumBenefits?.slice(0, 3)?.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-warning/5 rounded-lg">
                <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={benefit?.icon} size={16} className="text-warning" />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-foreground">{benefit?.title}</h5>
                  <p className="text-xs text-muted-foreground">{benefit?.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              variant="default"
              iconName="Crown"
              iconPosition="left"
              asChild
            >
              <Link to="/subscription-management">
                {subscriptionInfo?.type === 'guest' ? 'Sign In & Upgrade' : 'Upgrade to Premium'}
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              ✓ 30-day money-back guarantee
            </p>
          </div>
        </div>
      )}
      {/* Premium Member Exclusive Section */}
      {subscriptionInfo?.type === 'premium' && (
        <div className="space-y-4 border-t border-border pt-4">
          <div className="flex items-center space-x-2">
            <Icon name="Star" size={16} className="text-warning" />
            <h4 className="text-sm font-semibold text-foreground">Premium Exclusive</h4>
          </div>
          
          <div className="bg-gradient-to-r from-warning/10 to-primary/10 border border-warning/20 rounded-lg p-4">
            <div className="text-center space-y-2">
              <Icon name="Gift" size={24} className="text-warning mx-auto" />
              <h5 className="font-semibold text-foreground">Thank you for being Premium!</h5>
              <p className="text-sm text-muted-foreground">
                You have access to all CareerLift features and priority support
              </p>
            </div>
          </div>

          <div className="flex justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Download Invoice
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Share"
              iconPosition="left"
            >
              Refer Friends
            </Button>
          </div>
        </div>
      )}
      {/* Usage Stats for Premium Users */}
      {subscriptionInfo?.type === 'premium' && user?.usageStats && (
        <div className="space-y-3 border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-foreground">This Month's Usage</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="space-y-1">
              <div className="text-lg font-bold text-primary font-mono">
                {user?.usageStats?.plansGenerated || 0}
              </div>
              <div className="text-xs text-muted-foreground">Plans Generated</div>
            </div>
            <div className="space-y-1">
              <div className="text-lg font-bold text-success font-mono">
                {user?.usageStats?.exercisesCompleted || 0}
              </div>
              <div className="text-xs text-muted-foreground">Exercises Done</div>
            </div>
            <div className="space-y-1">
              <div className="text-lg font-bold text-warning font-mono">
                {user?.usageStats?.reportsExported || 0}
              </div>
              <div className="text-xs text-muted-foreground">Reports Exported</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;